// LinkOrCreateSection.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { getPluralLabel, getSingularLabel } from 'utilities/Languages';

// Hooks
import { useLinkOrCreate } from 'components/addmodal/sections/linkorcreate/useLinkOrCreate';

// CSS
import './LinkOrCreateSection.css';

// Components
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import TextInput from 'components/textinput/TextInput';

const getNodeTitle = (node) => String(node?.title || '').trim() || 'Untitled';

/**
 * LinkOrCreateSection
 *
 * Step 2 of the related-item add flow: create a new node or link existing ones.
 */
const LinkOrCreateSection = React.memo(({
  isOpen,
  typeKey,
  typeKeys = null,
  sourceType = '',
  listLabel = '',
  anchorNode,
  baseKey,
  user,
  onAttached,
  onClose,
  allowCreate = true,
}) => {
  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const { getType } = useNodeTypes();
  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();
  // ----- STATE ----- //

  const [hoveredNodeKey, setHoveredNodeKey] = useState('');

  // ----- MEMOS ----- //

  const resolvedTypeKeys = useMemo(() => {
    const fromList = (Array.isArray(typeKeys) ? typeKeys : [])
      .map((key) => String(key || '').trim())
      .filter(Boolean);
    if (fromList.length > 0) return fromList;
    const single = String(typeKey || '').trim();
    return single ? [single] : [];
  }, [typeKey, typeKeys]);

  const primaryTypeKey = String(typeKey || resolvedTypeKeys[0] || '').trim();

  const typeDef = useMemo(() => (
    primaryTypeKey ? getType(primaryTypeKey) : null
  ), [getType, primaryTypeKey]);

  const singularLabel = useMemo(() => (
    getSingularLabel(typeDef, primaryTypeKey || 'Item', 'en') || 'Item'
  ), [typeDef, primaryTypeKey]);

  const pluralLabel = useMemo(() => {
    const override = String(listLabel || '').trim();
    if (override) return override;
    return getPluralLabel(typeDef, primaryTypeKey || 'Items', 'en') || 'Items';
  }, [listLabel, typeDef, primaryTypeKey]);

  // ----- HOOKS ----- //

  const {
    filterTerm,
    handleFilterChange,
    filteredNodes,
    newTitle,
    handleNewTitleChange,
    isBusy,
    isCreating,
    isLinkingSelected,
    createError,
    selectedCount,
    isNodeSelected,
    handleToggleSelected,
    handleLinkSelected,
    handleCreateAndLink,
    titleSeededFromFilter,
  } = useLinkOrCreate({
    isOpen,
    isActive: resolvedTypeKeys.length > 0,
    typeKey: primaryTypeKey,
    typeKeys: resolvedTypeKeys,
    sourceType,
    baseKey,
    anchorNode,
    user,
    onAttached,
    onClose,
  });

  // ----- MEMOS ----- //

  const linkSelectedLabel = useMemo(() => {
    const verb = allowCreate ? 'Link' : 'Attach';
    if (isLinkingSelected) return `${verb === 'Link' ? 'Linking' : 'Attaching'}…`;
    if (selectedCount === 1) return `${verb} selected item`;
    return `${verb} ${selectedCount} selected items`;
  }, [allowCreate, isLinkingSelected, selectedCount]);

  // ----- RENDER ----- //

  return (
    <div className="link-or-create-section">

      {/* CREATE PANEL */}
      {allowCreate ? (
        <div
          className="link-or-create-section-panel link-or-create-section-create-panel"
        >

          {/* LABEL */}
          <div
            className="link-or-create-section-label"
          >
            {`Create New ${singularLabel}`}
          </div>

          <TextInput
            value={newTitle}
            placeholder={`${singularLabel} title…`}
            autoFocus={!isMobile}
            onChange={(event) => handleNewTitleChange(event.target.value)}
          />

          {/* HINT */}
          {titleSeededFromFilter ? (
            <div
              className="link-or-create-section-hint"
            >
              Title seeded from your filter — edit if needed
            </div>
          ) : null}

          {/* HINT */}
          {createError ? (
            <div
              className="link-or-create-section-hint"
              style={{
                color: theme.warning,
              }}
            >
              {createError}
            </div>
          ) : null}

          <Button
            label={isCreating ? 'Linking…' : 'Add and Link'}
            iconKey="link"
            enabled={Boolean(String(newTitle || '').trim()) && !isBusy}
            onClick={handleCreateAndLink}
          />
        </div>
      ) : null}

      {/* EXISTING PANEL */}
      <div
        className="link-or-create-section-panel link-or-create-section-existing-panel"
      >

        {/* LABEL */}
        <div
          className="link-or-create-section-label"
        >
          {allowCreate
            ? `Link to Existing ${pluralLabel}`
            : `Attach Existing ${pluralLabel}`}
        </div>

        <TextInput
          value={filterTerm}
          onChange={(event) => handleFilterChange(event.target.value)}
          placeholder={`Filter ${pluralLabel.toLowerCase()}…`}
          autoFocus={!allowCreate && !isMobile}
        />

        {/* LIST */}
        <div className="link-or-create-section-list">

          {/* EMPTY */}
          {!baseKey && (
            <div
              className="link-or-create-section-empty"
            >
              No active base.
            </div>
          )}

          {/* EMPTY */}
          {baseKey && filteredNodes.length === 0 && (
            <div
              className="link-or-create-section-empty"
            >
              {String(filterTerm || '').trim()
                ? `No matching ${pluralLabel.toLowerCase()}`
                : `No ${pluralLabel.toLowerCase()} yet`}
            </div>
          )}

          {baseKey && filteredNodes.map((node) => {
            const nodeKey = String(node?.key || '');
            const isSelected = isNodeSelected(nodeKey);
            const isHovered = hoveredNodeKey === nodeKey;

            return (
              <div
                className="link-or-create-section-row"
                style={{
                  backgroundColor: isHovered || isSelected ? theme.backgroundHover : theme.backgroundMuted,
                  borderColor: isHovered || isSelected ? theme.foregroundMuted : 'transparent',
                  cursor: isBusy ? 'not-allowed' : 'pointer',
                  opacity: isBusy ? 0.6 : 1,
                }}
                key={nodeKey}
                onClick={() => {
                  if (isBusy) return;
                  handleToggleSelected(node);
                }}
                onMouseEnter={() => setHoveredNodeKey(nodeKey)}
                onMouseLeave={() => setHoveredNodeKey('')}
              >
                <Checkbox value={isSelected} size={20} />

                {/* ROW TITLE */}
                <span className="link-or-create-section-row-title">{getNodeTitle(node)}</span>
              </div>
            );
          })}
        </div>

        {selectedCount > 0 ? (
          <Button
            label={linkSelectedLabel}
            iconKey="link"
            enabled={!isBusy}
            onClick={handleLinkSelected}
          />
        ) : null}
      </div>
    </div>
  );
});

// Dev Tools
LinkOrCreateSection.displayName = 'LinkOrCreateSection';

export default LinkOrCreateSection;
